import { Component, EventEmitter, Input, Output } from '@angular/core';
import { Expediente } from '@models/expediente.model';
import { Licencia } from '@models/licencia.model';
import { Prestador } from '@models/prestador.model';
import { AlertService } from '@services/alert.service';
import { ErrorService } from '@services/error.service';
import { PrestadorService } from '@services/prestador.service';
import { SessionService } from '@services/session.service';
import { StateService } from '@services/state.service';
import { TerminologiaService } from '@services/terminologia.service';
import ExpedienteEstados from '@shared/enums/expediente-estados.enum';
import { Mensajes } from '@shared/enums/mensajes.enum';
import Roles from '@shared/enums/roles.enum';
import { DangerError } from '@shared/errors/custom.error';
import { obtenerCantidadDias } from '@shared/utils/date.utils';
import sprintf from '@shared/utils/sprintf.util';
import { isValidEmail } from '@shared/utils/validators.utils';

@Component({
    selector: 'app-header-prestador',
    templateUrl: './header-prestador.component.html',
    styleUrls: ['./header-prestador.component.scss']
})
export class HeaderPrestadorComponent {
    @Input() Expediente: Expediente;
    @Input() Licencia: Licencia;
    @Input() canAddDoc: boolean;

    constructor(
        private prestadorService: PrestadorService,
        private toastr: AlertService,
        private errorService: ErrorService,
        private stateService: StateService,
        private terminologiaService: TerminologiaService,
        private sessionService: SessionService,
    ) { }


    prestador: Prestador;
    expediente: Expediente;
    emailUpdate = false; // Variable que maneja el renderizado del input para actualizar el correo alternativo
    correoPrestador = ""; // Variable que maneja el correo electrónico ingresado en el input correo alternativo
    isLoading = false; // Variable que maneja el renderizado del spinner de carga del boton actualizar correo

    ngOnInit() {
        this.prestador = this.stateService.getPrestador();
        this.expediente = this.stateService.getExpediente();
        this.correoPrestador = this.prestador.email_fiscalizacion;
    }

    get mensajeDiasRestantes() {
        if (!this.canAddDoc) return;
        const mensaje = this.terminologiaService.obtenerMensajePorID(this.diasRestantes < 1 ? Mensajes.dias_habiles_agotados : Mensajes.dias_habiles_restantes);
        return sprintf(mensaje, this.diasRestantes)
    }

    get diasRestantes() {
        const hoy = new Date(); // Fecha actual
        const limite = new Date(this.Expediente.fecha_termino); // Fecha límite
        const dias = obtenerCantidadDias(limite, hoy);
        return dias < 0 ? dias : this.stateService.getExpediente().dias_habiles??-1;
    }

    async mostrarActualizarCorreo() {
        this.correoPrestador = this.prestador.email_fiscalizacion;
        this.emailUpdate = true;
    }

    async noActualizarCorreo() {
        this.emailUpdate = false;
        this.correoPrestador = this.prestador.email_fiscalizacion;
    }

    async actualizarCorreo() {
        this.isLoading = true;
        try {
            if (!isValidEmail(this.correoPrestador)) throw new DangerError("El correo ingresado no es válido");

            const msg = await this.prestadorService.actualizarEmail(this.prestador.rut, this.correoPrestador);
            this.prestador.email_fiscalizacion = this.correoPrestador;
            this.stateService.updateEmail(this.prestador.email_fiscalizacion);
            this.noActualizarCorreo();
            this.toastr.success(msg);
        } catch (error) {
            this.errorService.handle(error, "Se produjo un error al actualizar el correo")
        } finally {
            this.isLoading = false;
        }
    }

	get esPerfilCorrectoDiasRestantes(): boolean {
		const idRol = this.sessionService.rolActual;
		switch (idRol) {
			case Roles.creador_expediente:
				return [
					ExpedienteEstados.apelacion_suseso,
					ExpedienteEstados.apelacion_aprobada,
					ExpedienteEstados.apelacion_rechazada,
					ExpedienteEstados.resolucion_apelada,
					ExpedienteEstados.resolucion_firmada,
				].includes(this.Expediente.estado.codigo);
			case Roles.presidente:
				return [
					ExpedienteEstados.ingresado,
					ExpedienteEstados.solicitud_prorroga,
					ExpedienteEstados.cerrado,
				].includes(this.Expediente.estado.codigo);
			case Roles.fiscalizador:
				return [
					ExpedienteEstados.enviado_fiscalizacion,
					// RFC4: expediente devuelto por el Presidente para su corrección
					ExpedienteEstados.en_correccion,
				].includes(this.Expediente.estado.codigo);
			case Roles.prestador:
				return [
					ExpedienteEstados.aprobado,
					ExpedienteEstados.solicitud_prorroga,
					ExpedienteEstados.prorroga_aprobada,
					ExpedienteEstados.prorroga_rechazada,
				].includes(this.Expediente.estado.codigo);
			default:
				return false;
		}
	}
}
